import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isNil} from '@shared/helpers/lang'

import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {CashReceiptIssueType, CashReceiptIssueUsage} from '@web/core/constants'
import {
    setIsEdit,
    setIssueType,
    setIssueNumberElement,
    toggleShouldBeSaved,
    setApplying,
    setIssueNumber,
    setCashReceiptInfo,
    CashReceiptState,
    getInitialState,
    getUsageType,
} from '@web/slices/cashReceipt/slice'

import {CashReceiptReducers, MakeSliceOptions} from './interfaces'

export default (options?: MakeSliceOptions) =>
    createSlice<CashReceiptState, CashReceiptReducers, 'cashReceipt', SliceSelectors<CashReceiptState>>({
        name: 'cashReceipt',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(setApplying, (state, {payload}) => {
                    if (isNil(payload)) {
                        state.issueType.current = payload
                    } else if (payload === CashReceiptIssueUsage.PERSONAL_INCOME_TAX) {
                        state.issueType.current = state.issueType.prev
                    } else {
                        state.issueType.current = CashReceiptIssueType.BUSINESS_REGISTRATION_NUMBER
                    }
                })
                .addCase(setIsEdit, (state, {payload}) => {
                    state.isEdit = payload
                })
                .addCase(setIssueType, (state, {payload}) => {
                    state.issueType.current = payload
                    state.prevUsageType = getUsageType(payload)
                    if (getUsageType(payload) === CashReceiptIssueUsage.PERSONAL_INCOME_TAX) {
                        state.issueType.prev = payload
                    }
                })
                .addCase(setIssueNumber, (state, {payload}) => {
                    const {numberType, value} = payload
                    state.issueNumber[numberType] = value
                })
                .addCase(setIssueNumberElement, (state, {payload}) => {
                    const {numberType, value, index} = payload
                    state.issueNumber[numberType][index] = value
                })
                .addCase(toggleShouldBeSaved, (state, {payload}) => {
                    state.shouldBeSaved = payload
                })

                .addCase(setCashReceiptInfo, (state, {payload}) => {
                    const {applied, issueNumber, issueType, shouldBeSaved} = payload

                    if (applied) {
                        state.issueType.prev = state.issueType.current
                        state.issueType.current = issueType
                        state.issueNumber[issueType] = issueNumber
                    } else {
                        state.issueType.prev = null
                        state.issueType.current = null
                        state.issueNumber = getInitialState().issueNumber
                    }

                    state.shouldBeSaved = shouldBeSaved
                })
            options?.extraReducers?.(builder)
            builder.addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                return payload.cashReceipt ?? state
            })
        },
    })
